// 서버 rate limit(429) 전역 신호 (2026-07-26 · 버킷 분리 2026-07-30 N-25)
//
// 배경: 화면들이 실패 사유를 구분하지 않고 전부 "네트워크를 확인해주세요"라고 안내했다.
//   실제로는 서버가 429로 차단한 것이었고(prod 로그 19:18 — 68건 전부 429), 그 문구 때문에
//   원인을 VPN·통신 문제로 오해하게 됐다. 네트워크는 멀쩡했다.
//
// netHealth(전송계층 실패=VPN·DNS 후킹 감지)와 **다른 축**이라 파일을 나눈다.
//   netHealth = "서버에 닿지도 못함" / 여기 = "서버가 닿았고 '잠시 멈추라'고 답함".
//
// ⚠ N-25 (2026-07-30): 상태가 버킷 구분 없는 단일 `blockedUntil`이었다. 그래서
//   ⓐ 쓰기 요청이 429로 막힌 직후 병렬 시세 읽기가 200을 받으면 **아직 유효한 안내가
//     즉시 사라지고** 다시 네트워크 오류 문구가 떴고, 반대로
//   ⓑ 읽기 버킷의 429가 쓰기 오류 화면까지 오염시켰다.
//   → 버킷별로 보관하고 **해당 버킷의 성공만** 그 버킷을 해제한다.
//   버킷 이름은 추측하지 않는다 — 서버가 `X-RateLimit-Bucket`으로 알려준다(단일 출처).
import { useSyncExternalStore } from 'react';

/** 서버가 Retry-After를 안 줄 때 가정할 대기 시간(초) — 미들웨어 정책이 60s 창이다. */
const DEFAULT_RETRY_AFTER_SEC = 60;

/** 서버가 버킷을 알려주지 않은 경우(구버전 응답·프록시가 헤더를 지운 경우)의 기본 키. */
export const UNKNOWN_BUCKET = 'unknown';

/** 버킷 → 차단 만료 시각(epoch ms). 지난 항목은 만료로 취급한다. */
const blockedUntilByBucket = new Map<string, number>();
/** 버킷 → 만료 시각에 emit할 타이머 (N-27). */
const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/**
 * ⚠ N-27 (2026-07-30): "1분 뒤 자동으로 풀려요"가 **시간 경과만으로는 절대 사라지지 않았다.**
 *   snapshot은 Date.now()로 만료를 계산하는데 만료 시각에 listener를 부르는 타이머가 없어서
 *   React가 snapshot을 다시 읽지 않았다 → 429 화면을 그대로 보고 있으면 제한이 풀린 뒤에도
 *   문구가 남는다(화면이 약속한 자동 해제가 실제로는 일어나지 않았다).
 *   → 만료 시각에 emit하는 타이머를 걸고, 같은 버킷의 새 429는 기존 타이머를 교체한다.
 */
function scheduleExpiry(bucket: string, delayMs: number) {
  const prev = expiryTimers.get(bucket);
  if (prev) clearTimeout(prev);
  const timer = setTimeout(() => {
    expiryTimers.delete(bucket);
    // 만료됐다면 지도에서도 지운다(isRateLimited는 시각 비교라 이미 false지만
    // 상태를 남겨두면 clearRateLimited가 불필요하게 emit한다).
    const until = blockedUntilByBucket.get(bucket);
    if (until != null && until <= Date.now()) blockedUntilByBucket.delete(bucket);
    emit(); // ← 이 호출이 없어서 문구가 남아 있었다
  }, Math.max(0, delayMs));
  // Node(테스트) 환경에서 프로세스를 붙잡지 않게 — 브라우저 타이머엔 unref가 없다
  (timer as unknown as { unref?: () => void }).unref?.();
  expiryTimers.set(bucket, timer);
}

/**
 * apiFetch가 429 응답을 받았을 때 호출.
 * @param retryAfterSec 서버 Retry-After 헤더값
 * @param bucket 서버 X-RateLimit-Bucket 헤더값(없으면 unknown)
 */
export function recordRateLimited(retryAfterSec?: number | null, bucket?: string | null): void {
  const sec = Number.isFinite(retryAfterSec) && (retryAfterSec as number) > 0
    ? Math.min(retryAfterSec as number, 300) // 비정상적으로 긴 값은 잘라낸다(문구가 영원히 남지 않게)
    : DEFAULT_RETRY_AFTER_SEC;
  const key = bucket || UNKNOWN_BUCKET;
  blockedUntilByBucket.set(key, Date.now() + sec * 1000);
  scheduleExpiry(key, sec * 1000);
  emit();
}

/**
 * 정상 응답을 받으면 **그 버킷만** 해제한다.
 * 🔑 다른 버킷을 건드리지 않는 것이 N-25의 핵심이다 —
 *    시세 읽기 성공이 주문 429 안내를 지우면 사용자는 원인을 다시 오해한다.
 */
export function clearRateLimited(bucket?: string | null): void {
  const key = bucket || UNKNOWN_BUCKET;
  if (!blockedUntilByBucket.has(key)) return;
  blockedUntilByBucket.delete(key);
  const timer = expiryTimers.get(key);
  if (timer) { clearTimeout(timer); expiryTimers.delete(key); }
  emit();
}

/**
 * 지금 rate limit 상태인가 (시간이 지나면 자동으로 false).
 * @param bucket 지정하면 그 버킷만, 생략하면 아무 버킷이든 차단 중인지
 */
export function isRateLimited(bucket?: string): boolean {
  const now = Date.now();
  if (bucket) return (blockedUntilByBucket.get(bucket) ?? 0) > now;
  for (const until of blockedUntilByBucket.values()) {
    if (until > now) return true;
  }
  return false;
}

export function subscribeRateLimit(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** 테스트 전용 — 모듈 전역 상태 초기화 */
export function resetRateLimitForTest(): void {
  blockedUntilByBucket.clear();
  for (const timer of expiryTimers.values()) clearTimeout(timer);
  expiryTimers.clear();
  listeners.clear();
}

/** 429 안내 문구 단일 출처 — 화면마다 다르게 쓰지 않도록 여기서만 정의한다. */
export const RATE_LIMITED_TEXT = '요청이 잠시 몰렸어요 · 1분 뒤 자동으로 풀려요';

/**
 * 로딩 실패 안내 문구. 429면 사실대로 알리고, 아니면 화면이 쓰던 기존 문구를 그대로 돌려준다.
 * @param fallback 기존 문구(네트워크 안내 등)
 * @param bucket   이 화면이 관심 있는 버킷(조회 화면은 'read'). 생략하면 아무 버킷이든.
 *
 * ⚠ 감사(N-25)는 "전역 추측값 대신 발생한 ApiError의 status를 직접 전달"도 권했다.
 *   그 전환은 화면들의 오류 상태 배선을 함께 바꿔야 해서 분리했다. 버킷 인자가 중간 단계다 —
 *   조회 화면이 쓰기 버킷의 429에 오염되지 않게 한다.
 */
export function useLoadErrorText(fallback: string, bucket?: string): string {
  const limited = useSyncExternalStore(
    subscribeRateLimit,
    () => isRateLimited(bucket),
    () => false,
  );
  return limited ? RATE_LIMITED_TEXT : fallback;
}
